import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableConfigDialog } from "../tables/TableConfigDialog";
import { phoneLogsFiltersAreEqual, type PhoneLogsFilters } from "./phoneLogsFilters";
import { PhoneLogsFilterConfigForm } from "./PhoneLogsFilterConfigForm";
import { ColumnConfigForm, type ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";

export interface PhoneLogsTableConfigDialogProps
  extends Pick<UseTableViewConfigResult<PhoneLogsFilters>, "onApplyConfig" | "activeFilters"> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClearAll?: () => void;
  title?: string;
  description?: string;
  columnConfigFormProps: Pick<
    ColumnConfigFormProps,
    "columnVisibility" | "orderedVisibleColumnIds" | "combinedFieldToColumnConfigMap" | "title"
  >;
  isDirty?: boolean;
  directionOptions: string[];
  statusOptions: string[];
}

export const PhoneLogsTableConfigDialog = ({
  onApplyConfig,
  activeFilters,
  onClearAll,
  title,
  description,
  columnConfigFormProps: {
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    title: columnConfigTitle,
  },
  isDirty: providedIsDirty,
  directionOptions,
  statusOptions,
  ...rest
}: PhoneLogsTableConfigDialogProps) => {
  const { t } = useTranslation("settings");

  const [stagedColumnIds, setStagedColumnIds] = useState<string[]>(orderedVisibleColumnIds);
  const [stagedFilters, setStagedFilters] = useState<PhoneLogsFilters>(activeFilters);

  const isColumnsDirty = useMemo(() => {
    if (stagedColumnIds.length !== orderedVisibleColumnIds.length) return true;
    return stagedColumnIds.some((id, i) => id !== orderedVisibleColumnIds[i]);
  }, [stagedColumnIds, orderedVisibleColumnIds]);

  const isFiltersDirty = useMemo(
    () => !phoneLogsFiltersAreEqual(stagedFilters, activeFilters),
    [stagedFilters, activeFilters]
  );

  const isDirty = providedIsDirty || isColumnsDirty || isFiltersDirty;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setStagedColumnIds(orderedVisibleColumnIds);
      setStagedFilters(activeFilters);
    }
    rest.onOpenChange?.(open);
  };

  const handleApply = () => {
    onApplyConfig(stagedColumnIds, stagedFilters);
  };

  return (
    <TableConfigDialog
      {...rest}
      onOpenChange={handleOpenChange}
      onClearAll={onClearAll}
      title={title ?? t("Configure Logs Table View")}
      description={description ?? t("Add and remove columns, and apply filters to narrow down the logs")}
      isDirty={isDirty}
      onApply={handleApply}
    >
      <ColumnConfigForm
        columnVisibility={columnVisibility}
        orderedVisibleColumnIds={orderedVisibleColumnIds}
        combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
        stagedColumnIds={stagedColumnIds}
        onStagedColumnIdsChange={setStagedColumnIds}
        title={columnConfigTitle}
        isDirty={isColumnsDirty}
        reset={() => setStagedColumnIds(orderedVisibleColumnIds)}
      />
      <PhoneLogsFilterConfigForm
        stagedFilters={stagedFilters}
        onStagedFiltersChange={setStagedFilters}
        directionOptions={directionOptions}
        statusOptions={statusOptions}
      />
    </TableConfigDialog>
  );
};
